import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useFormules } from "@/store/formules/FormulesProvider";
import { formatFcfa } from "@/utils/format";
import {
  LIMITE_KEYS,
  LIMITE_LABELS,
  formatFormuleLimite,
  remiseAnnuellePourcent,
  type FormuleCatalogue,
} from "@/config/formules";
import ConfirmationModal from "@/pages/super-admin/components/ConfirmationModal";
import FormuleForm from "@/pages/super-admin/components/FormuleForm";

interface PendingToggle {
  formule: FormuleCatalogue;
  nextStatut: "active" | "desactivee";
}

export default function SuperAdminFormulesPage() {
  const { formules, toggleStatut } = useFormules();
  const [editing, setEditing] = useState<FormuleCatalogue | null>(null);
  const [pending, setPending] = useState<PendingToggle | null>(null);

  const stats = useMemo(
    () => ({
      total: formules.length,
      actives: formules.filter((formule) => formule.statut === "active").length,
      desactivees: formules.filter((formule) => formule.statut === "desactivee").length,
      illimitees: formules.filter((formule) => formule.limites.immeubles === null).length,
    }),
    [formules]
  );

  const confirmToggle = () => {
    if (!pending) return;
    toggleStatut(pending.formule.id);
    setPending(null);
  };

  const columns: DataTableColumn<FormuleCatalogue>[] = [
    {
      key: "formule",
      header: "Formule",
      className: "min-w-[200px]",
      render: (formule) => (
        <div className="flex flex-col">
          <span className="font-label font-medium text-foreground-950">{formule.nom}</span>
          <span className="text-xs text-foreground-500">{formule.tagline}</span>
        </div>
      ),
    },
    {
      key: "mensuel",
      header: "Mensuel",
      align: "right",
      className: "min-w-[110px]",
      render: (formule) => (
        <span className="font-label font-medium text-foreground-950">
          {formule.prixMensuel === 0 ? "0 FCFA" : formatFcfa(formule.prixMensuel)}
        </span>
      ),
    },
    {
      key: "annuel",
      header: "Annuel",
      align: "right",
      className: "min-w-[130px]",
      render: (formule) => (
        <div className="flex flex-col items-end">
          <span className="font-label font-medium text-foreground-950">
            {formule.prixAnnuel === 0 ? "0 FCFA" : formatFcfa(formule.prixAnnuel)}
          </span>
          {remiseAnnuellePourcent(formule) > 0 && (
            <span className="text-[11px] text-accent-700">−{remiseAnnuellePourcent(formule)}%</span>
          )}
        </div>
      ),
    },
    ...LIMITE_KEYS.map<DataTableColumn<FormuleCatalogue>>((key) => ({
      key: `limite-${key}`,
      header: LIMITE_LABELS[key],
      align: "center",
      className: "min-w-[110px]",
      render: (formule) => (
        <span
          className={formule.limites[key] === null ? "font-label font-medium text-secondary-700" : "text-foreground-700"}
        >
          {formatFormuleLimite(formule.limites[key])}
        </span>
      ),
    })),
    {
      key: "statut",
      header: "Statut",
      render: (formule) => (
        <Badge
          tone={formule.statut === "active" ? "secondary" : "neutral"}
          icon={formule.statut === "active" ? "ri-checkbox-circle-line" : "ri-forbid-2-line"}
        >
          {formule.statut === "active" ? "Active" : "Désactivée"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[230px]",
      render: (formule) => {
        const active = formule.statut === "active";
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() =>
                setPending({
                  formule,
                  nextStatut: active ? "desactivee" : "active",
                })
              }
              className={
                active
                  ? "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                  : "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-secondary-300 bg-secondary-50 px-3.5 py-2 text-xs font-label font-medium text-secondary-800 transition-colors hover:bg-secondary-100 cursor-pointer"
              }
            >
              <i className={active ? "ri-forbid-2-line text-base" : "ri-checkbox-circle-line text-base"} aria-hidden="true" />
              {active ? "Désactiver" : "Activer"}
            </button>
            <CardActions onEdit={() => setEditing(formule)} editLabel="Modifier la formule" />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-3xl">
        <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Formules</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
          Source de vérité unique des abonnements. Ces prix et limites alimentent la page publique Tarifs,
          l'inscription, l'espace Propriétaire et l'espace Agence. Désactiver une formule la retire des nouvelles
          souscriptions sans supprimer les abonnements en cours.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-vip-diamond-line" label="Formules" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actives" value={String(stats.actives)} tone="secondary" />
        <StatCard icon="ri-forbid-2-line" label="Désactivées" value={String(stats.desactivees)} tone="accent" />
        <StatCard icon="ri-infinity-line" label="Offres illimitées" value={String(stats.illimitees)} tone="neutral" />
      </div>

      <DataTable
        columns={columns}
        rows={formules}
        rowKey={(formule) => formule.id}
        minWidthClass="min-w-[1180px]"
        caption="Formules"
      />

      <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Cohérence des tarifs</h3>
        <p className="mt-1 text-xs text-foreground-600">
          Récapitulatif des prix mensuels et annuels utilisés partout dans la plateforme.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-background-200 text-left">
                <th scope="col" className="py-2.5 pr-4 font-label font-semibold text-foreground-500">Formule</th>
                <th scope="col" className="py-2.5 pr-4 font-label font-semibold text-foreground-500">Mensuel</th>
                <th scope="col" className="py-2.5 pr-4 font-label font-semibold text-foreground-500">Annuel</th>
                <th scope="col" className="py-2.5 font-label font-semibold text-foreground-500">Statut</th>
              </tr>
            </thead>
            <tbody>
              {formules.map((formule) => (
                <tr key={formule.id} className="border-b border-background-200 last:border-0">
                  <td className="py-2.5 pr-4 font-label font-medium text-foreground-950">{formule.nom}</td>
                  <td className="py-2.5 pr-4 text-foreground-700">
                    {formule.prixMensuel === 0 ? "Gratuit" : formatFcfa(formule.prixMensuel)}
                  </td>
                  <td className="py-2.5 pr-4 text-foreground-700">
                    {formule.prixAnnuel === 0 ? "Gratuit" : formatFcfa(formule.prixAnnuel)}
                  </td>
                  <td className="py-2.5 text-foreground-600">
                    {formule.statut === "active" ? "Active" : "Désactivée"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Link
          to="/tarifs"
          className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
        >
          Voir la page publique Tarifs
          <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
        </Link>
      </section>

      <FormuleForm open={Boolean(editing)} onClose={() => setEditing(null)} formule={editing} />

      <ConfirmationModal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={confirmToggle}
        title={pending?.nextStatut === "active" ? "Activer la formule" : "Désactiver la formule"}
        description={
          pending
            ? pending.nextStatut === "active"
              ? `Réactiver la formule « ${pending.formule.nom} » ? Elle redeviendra proposée à la souscription.`
              : `Désactiver la formule « ${pending.formule.nom} » ? Elle ne sera plus proposée aux nouvelles souscriptions. Les abonnements en cours ne sont pas supprimés.`
            : ""
        }
        confirmLabel={pending?.nextStatut === "active" ? "Activer" : "Désactiver"}
        icon={pending?.nextStatut === "active" ? "ri-checkbox-circle-line" : "ri-forbid-2-line"}
        tone={pending?.nextStatut === "active" ? "primary" : "danger"}
      />
    </div>
  );
}